import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useRBAC } from '@/contexts/rbac-context';
import {
  fetchCompanies,
  createCompany,
  uploadCompanyLogo,
  updateCompanyBranding,
  type CompanySummary,
  type CompanyProvisioningResult,
} from '@/lib/admin-companies';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { ArrowLeft, Building2, Copy, Plus, RefreshCw } from 'lucide-react';

const EMPTY_CREATE_FORM = {
  compCd: '',
  compName: '',
  bizNo: '',
  repDirector: '',
  repPhone: '',
  adminEmail: '',
};

// 온보딩 진행 상태. 진행중(PENDING/ORG_PENDING)은 오류가 아니므로 중립 배지로 표시한다.
const STATUS_LABEL: Record<string, string> = {
  PENDING: '생성 대기',
  ORG_PENDING: '조직 생성 대기',
  ACTIVE: '정상',
  FAILED: '실패',
};

export function CompanyManagementPage() {
  const { isSuperAdmin, loading: rbacLoading } = useRBAC();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // 생성 다이얼로그
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  // 온보딩 결과(임시 비밀번호 1회 표시)
  const [provisionResult, setProvisionResult] = useState<CompanyProvisioningResult | null>(null);

  // 브랜딩 다이얼로그
  const [brandingTarget, setBrandingTarget] = useState<CompanySummary | null>(null);
  const [brandingForm, setBrandingForm] = useState<{ brandName: string; logoUrl: string }>({
    brandName: '',
    logoUrl: '',
  });
  // 로고는 저장 시점에 업로드한다(취소 시 고아 파일이 남지 않도록).
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');

  // 미리보기 URL 은 선택된 파일의 파생값이라 정리도 여기서 한 번에 한다.
  useEffect(() => {
    if (!logoFile) {
      setLogoPreview('');
      return;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCompanies(await fetchCompanies());
    } catch {
      toast({ title: '회사 목록 조회 실패', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) void load();
  }, [isSuperAdmin, load]);

  if (!rbacLoading && !isSuperAdmin) {
    return <Navigate to='/' replace />;
  }

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const result = await createCompany({
        compCd: createForm.compCd.trim().toUpperCase(),
        compName: createForm.compName.trim(),
        bizNo: createForm.bizNo.trim() || undefined,
        repDirector: createForm.repDirector.trim() || undefined,
        repPhone: createForm.repPhone.trim() || undefined,
        adminEmail: createForm.adminEmail.trim(),
      });
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE_FORM);
      setProvisionResult(result);
      await load();
    } catch {
      toast({ title: '회사 생성 실패', description: '온보딩 처리 중 오류가 발생했습니다. 같은 요청으로 재시도하면 이어서 진행됩니다.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!provisionResult?.initialAdminPassword) return;
    try {
      await navigator.clipboard.writeText(provisionResult.initialAdminPassword);
      toast({ title: '임시 비밀번호를 복사했습니다.' });
    } catch {
      toast({ title: '복사 실패', description: '표시된 값을 직접 선택해 복사해주세요.', variant: 'destructive' });
    }
  };

  const handleOpenBranding = (company: CompanySummary) => {
    setBrandingTarget(company);
    setBrandingForm({ brandName: company.brandName ?? '', logoUrl: company.logoUrl ?? '' });
    setLogoFile(null);
  };

  const handleCloseBranding = () => {
    setBrandingTarget(null);
    setLogoFile(null);
  };

  const handleLogoFile = (file: File | undefined) => {
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast({ title: '로고 용량 초과', description: '1MB 이하 파일만 업로드할 수 있습니다.', variant: 'destructive' });
      return;
    }
    setLogoFile(file);
  };

  const handleSaveBranding = async () => {
    if (!brandingTarget) return;
    setSubmitting(true);
    try {
      const logoUrl = logoFile
        ? await uploadCompanyLogo(brandingTarget.compCd, logoFile)
        : brandingForm.logoUrl.trim() || null;
      await updateCompanyBranding(brandingTarget.compCd, {
        brandName: brandingForm.brandName.trim() || null,
        logoUrl,
      });
      toast({ title: '브랜딩이 저장되었습니다.', description: '다음 로그인 페이지부터 적용됩니다.' });
      handleCloseBranding();
      await load();
    } catch {
      toast({ title: '브랜딩 저장 실패', description: '저장 중 오류가 발생했습니다. 다시 시도해주세요.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='container mx-auto py-3 space-y-3 max-w-5xl'>
      <Button variant='ghost' size='sm' className='-ml-2' onClick={() => navigate('/')}>
        <ArrowLeft className='size-4 mr-1' />
        메인으로
      </Button>

      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle className='flex items-center gap-2'>
                <Building2 className='size-5' />
                회사(테넌트) 관리
              </CardTitle>
              <CardDescription className='text-white/75'>테넌트를 생성하고 로그인 페이지 브랜딩을 관리합니다.</CardDescription>
            </div>
            <div className='flex items-center gap-2'>
              <Button size='sm' variant='secondary' onClick={() => void load()} disabled={loading}>
                <RefreshCw className={`size-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                새로고침
              </Button>
              <Button size='sm' onClick={() => setCreateOpen(true)}>
                <Plus className='size-4 mr-1' />
                회사 생성
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className='flex items-center justify-center py-12 text-muted-foreground text-sm'>
              불러오는 중...
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>코드</TableHead>
                  <TableHead>회사명</TableHead>
                  <TableHead>브랜드 타이틀</TableHead>
                  <TableHead>로고</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead className='text-right'>액션</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className='py-12 text-center text-sm text-muted-foreground'>
                      등록된 회사가 없습니다.
                    </TableCell>
                  </TableRow>
                )}
                {companies.map(company => (
                  <TableRow key={company.compCd}>
                    <TableCell className='font-mono'>{company.compCd}</TableCell>
                    <TableCell className='font-medium'>{company.compName ?? '-'}</TableCell>
                    <TableCell>{company.brandName ?? '-'}</TableCell>
                    <TableCell>
                      {company.logoUrl ? (
                        <img src={company.logoUrl} alt={company.brandName ?? company.compCd} className='h-6 max-w-24 object-contain' />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <div className='flex items-center gap-1'>
                        <Badge
                          variant={
                            company.provisioningStatus === 'ACTIVE'
                              ? 'secondary'
                              : company.provisioningStatus === 'FAILED'
                                ? 'destructive'
                                : 'outline'
                          }
                        >
                          {company.provisioningStatus
                            ? STATUS_LABEL[company.provisioningStatus] ?? company.provisioningStatus
                            : '-'}
                        </Badge>
                        {company.useYn === 'N' && <Badge variant='destructive'>비활성</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className='text-right'>
                      <Button size='sm' variant='outline' onClick={() => handleOpenBranding(company)}>
                        브랜딩
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* 회사 생성 다이얼로그 */}
      <Dialog open={createOpen} onOpenChange={open => { if (!open) setCreateOpen(false); }}>
        <DialogContent className='sm:max-w-md max-h-[85vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>회사 생성 (테넌트 온보딩)</DialogTitle>
            <DialogDescription>
              Keycloak 조직 생성과 최초 관리자 계정 발급까지 자동으로 진행됩니다.
            </DialogDescription>
          </DialogHeader>
          {/* 필수값·형식 검증은 브라우저 기본 검증에 맡긴다(해당 입력에 바로 표시된다) */}
          <form onSubmit={e => { e.preventDefault(); void handleCreate(); }}>
            <div className='space-y-3 py-2'>
              <div className='space-y-1.5'>
                <Label htmlFor='comp-cd'>회사코드 * (영문/숫자/하이픈, 최대 10자 — 대문자로 저장)</Label>
                <Input
                  id='comp-cd'
                  value={createForm.compCd}
                  onChange={e => setCreateForm(prev => ({ ...prev, compCd: e.target.value.toUpperCase() }))}
                  placeholder='ACME'
                  required
                  maxLength={10}
                  // pattern은 v 플래그로 컴파일되어 문자클래스 안의 하이픈을 이스케이프해야 한다
                  pattern='[A-Z0-9][A-Z0-9\-]*'
                  title='영문 대문자·숫자로 시작하고 하이픈만 사용할 수 있습니다.'
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='comp-name'>회사명 *</Label>
                <Input id='comp-name' value={createForm.compName} onChange={e => setCreateForm(prev => ({ ...prev, compName: e.target.value }))} required />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='admin-email'>관리자 이메일 *</Label>
                <Input id='admin-email' type='email' value={createForm.adminEmail} onChange={e => setCreateForm(prev => ({ ...prev, adminEmail: e.target.value }))} required />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='biz-no'>사업자번호</Label>
                <Input id='biz-no' value={createForm.bizNo} onChange={e => setCreateForm(prev => ({ ...prev, bizNo: e.target.value }))} />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='rep-director'>대표자</Label>
                <Input id='rep-director' value={createForm.repDirector} onChange={e => setCreateForm(prev => ({ ...prev, repDirector: e.target.value }))} />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='rep-phone'>대표 전화</Label>
                <Input id='rep-phone' value={createForm.repPhone} onChange={e => setCreateForm(prev => ({ ...prev, repPhone: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button type='button' variant='outline' onClick={() => setCreateOpen(false)} disabled={submitting}>취소</Button>
              <Button type='submit' disabled={submitting}>생성</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 온보딩 결과(임시 비밀번호 1회 표시) — 재조회가 불가능하므로 확인 버튼으로만 닫는다 */}
      <AlertDialog open={!!provisionResult}>
        <AlertDialogContent
          onEscapeKeyDown={e => e.preventDefault()}
          className='max-h-[85vh] overflow-y-auto'
        >
          <AlertDialogHeader>
            <AlertDialogTitle>회사 생성 완료: {provisionResult?.compCd}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className='space-y-2'>
                <p>{provisionResult?.message}</p>
                {provisionResult?.initialAdminPassword ? (
                  <div className='space-y-1'>
                    <p className='font-medium text-destructive'>
                      최초 관리자 임시 비밀번호 — 지금만 표시되며 다시 조회할 수 없습니다. 안전한 경로로 관리자에게 전달하세요.
                    </p>
                    <div className='flex items-center gap-2'>
                      <code className='flex-1 rounded bg-muted px-3 py-2 font-mono text-sm select-all'>
                        {provisionResult.initialAdminPassword}
                      </code>
                      <Button size='sm' variant='outline' onClick={() => void handleCopyPassword()}>
                        <Copy className='size-4 mr-1' />
                        복사
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className='text-muted-foreground'>임시 비밀번호는 발급되지 않았습니다(기존 관리자 재사용 또는 재개).</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setProvisionResult(null)}>확인했습니다</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 브랜딩 수정 다이얼로그 */}
      <Dialog open={!!brandingTarget} onOpenChange={open => { if (!open) handleCloseBranding(); }}>
        <DialogContent className='sm:max-w-md max-h-[85vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>브랜딩 수정: {brandingTarget?.compCd}</DialogTitle>
            <DialogDescription>로그인 페이지의 로고와 타이틀을 설정합니다. 비워두면 기본(PetroHQ)으로 표시됩니다.</DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-2'>
            <div className='space-y-1.5'>
              <Label htmlFor='brand-name'>브랜드 타이틀 (최대 60자)</Label>
              <Input id='brand-name' value={brandingForm.brandName} maxLength={60} onChange={e => setBrandingForm(prev => ({ ...prev, brandName: e.target.value }))} placeholder='PetroHQ' />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='logo-file'>로고 (PNG/JPEG/SVG, 1MB 이하 — 저장 시 업로드)</Label>
              <Input id='logo-file' type='file' accept='image/png,image/jpeg,image/svg+xml' onChange={e => handleLogoFile(e.target.files?.[0])} />
              {(logoPreview || brandingForm.logoUrl) && (
                <div className='flex items-center gap-3 rounded border p-2'>
                  <img src={logoPreview || brandingForm.logoUrl} alt='로고 미리보기' className='h-10 max-w-32 object-contain' />
                  <Button
                    size='sm'
                    variant='ghost'
                    onClick={() => {
                      setLogoFile(null);
                      setBrandingForm(prev => ({ ...prev, logoUrl: '' }));
                    }}
                  >
                    제거
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={handleCloseBranding} disabled={submitting}>취소</Button>
            <Button onClick={handleSaveBranding} disabled={submitting}>저장</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
